import { Component, computed, inject } from '@angular/core';
import { TranslocoPipe } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { ModalName, ModalService } from '../../../../core/services/modal.service';
import { LoyaltyService } from '../../../../core/services/loyalty.service';
import { ErrorsService } from '../../../../core/services/errors.service';
import { AuditService } from '../../../../core/services/audit.service';
import { RoundIconComponent } from '../../../../shared/round-icon/round-icon.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { AsyncButtonDirective } from '../../../../shared/directives/async-button.directive';
import { IconNameType } from '../../../../shared/icons/icon-name-type.enum';
import { BillingPeriodKey } from '../billing-period-key.enum';

const TRANSLATION_ROOT = 'modals.reactivate_subscription_modal';

export interface ReactivateSubscriptionModalState {
  price?: number;
  billingPeriodKey?: BillingPeriodKey;
  nextPaymentDate?: string;
}

@Component({
  selector: 'cwo-reactivate-subscription-modal',
  standalone: true,
  imports: [TranslocoPipe, RoundIconComponent, IconComponent, AsyncButtonDirective],
  templateUrl: './reactivate-subscription-modal.component.html',
  styleUrl: './reactivate-subscription-modal.component.scss',
})
export class ReactivateSubscriptionModalComponent {
  private modalService = inject(ModalService);
  private loyaltyService = inject(LoyaltyService);
  private errorsService = inject(ErrorsService);
  private auditService = inject(AuditService);

  readonly iconNameType = IconNameType;
  readonly translationRoot = TRANSLATION_ROOT;

  public loadingSubscription: Subscription | null = null;
  public allErrors: string[] = [];

  private state = computed<ReactivateSubscriptionModalState>(
    () => this.modalService.stateForModal(ModalName.ReactivateSubscription) ?? {}
  );

  readonly price = computed(() => `$${(this.state().price ?? 0).toFixed(2)}`);

  readonly billingPeriodKey = computed(() => this.state().billingPeriodKey ?? BillingPeriodKey.Monthly);

  readonly nextPaymentDate = computed(() => this.state().nextPaymentDate ?? '');

  public closeModal(): void {
    this.modalService.closeModal(ModalName.ReactivateSubscription);
  }

  public reactivateSubscription(): void {
    if (this.loadingSubscription) return;

    this.allErrors = [];
    this.loadingSubscription = this.loyaltyService.reactivateSubscription().subscribe({
      next: () => {
        this.loadingSubscription = null;
        this.closeModal();
      },
      error: (error) => {
        this.loadingSubscription = null;
        this.setAllErrors(error);
      },
    });
  }

  private setAllErrors(error: any): void {
    const parsedError = this.errorsService.parseErrorResponse(error);
    this.auditService.createAudit(() => `Subscription Reactivation Errors: ${parsedError.allErrors}`);

    this.allErrors = parsedError.message ? [parsedError.message] : parsedError.allErrors;
  }
}
